import { Star, Quote } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Testimonial {
  name: string;
  location?: string;
  content: string;
  rating: number;
  avatar?: string;
}

interface TestimonialsSectionProps {
  content: {
    title?: string;
    subtitle?: string;
    testimonials?: Testimonial[];
  };
  variant?: string;
  layoutStyle?: string;
}

export function TestimonialsSection({ content, variant = 'default', layoutStyle = 'classic' }: TestimonialsSectionProps) {
  const {
    title = 'What Our Clients Say',
    subtitle,
    testimonials = [],
  } = content;

  const isMinimal = layoutStyle === 'modern-minimal';
  const isSerif = layoutStyle === 'elegant-serif';
  const isPlayful = layoutStyle === 'playful-rounded';

  if (testimonials.length === 0) return null;

  // Variant: Single Large (one featured testimonial)
  if (variant === 'single-large' && testimonials.length > 0) {
    const featured = testimonials[0];
    return (
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Quote className="w-12 h-12 text-primary/20 mx-auto mb-6" />
            <p className="text-2xl md:text-3xl text-foreground font-light mb-8 leading-relaxed">
              "{featured.content}"
            </p>
            <div className="flex items-center justify-center gap-4">
              {featured.avatar && (
                <img 
                  src={featured.avatar} 
                  alt={featured.name}
                  className="w-14 h-14 rounded-full object-cover"
                />
              )}
              <div className="text-left">
                <p className="font-semibold text-foreground">{featured.name}</p>
                {featured.location && (
                  <p className="text-muted-foreground text-sm">{featured.location}</p>
                )}
              </div>
            </div>
            {featured.rating > 0 && (
              <div className="flex justify-center mt-4">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={cn(
                      "w-5 h-5",
                      i < featured.rating ? "text-amber-500 fill-amber-500" : "text-muted"
                    )} 
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Variant: Grid (masonry-like grid)
  if (variant === 'grid') {
    return (
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          {title && (
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-display font-bold text-foreground mb-4 text-3xl md:text-4xl">
                {title}
              </h2>
              {subtitle && (
                <p className="text-muted-foreground text-lg">{subtitle}</p>
              )}
            </div>
          )}

          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="break-inside-avoid p-6 bg-card rounded-2xl shadow-sm border"
              >
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={cn(
                        "w-4 h-4",
                        i < testimonial.rating ? "text-amber-500 fill-amber-500" : "text-muted"
                      )} 
                    />
                  ))}
                </div>
                <p className="text-foreground mb-4">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  {testimonial.avatar && (
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p className="font-semibold text-foreground text-sm">{testimonial.name}</p>
                    {testimonial.location && (
                      <p className="text-muted-foreground text-xs">{testimonial.location}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Variant: Carousel style (horizontal scroll)
  if (variant === 'carousel') {
    return (
      <section className="section-padding bg-background overflow-hidden">
        <div className="container mx-auto px-4">
          {title && (
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-display font-bold text-foreground mb-4 text-3xl md:text-4xl">
                {title}
              </h2>
              {subtitle && (
                <p className="text-muted-foreground text-lg">{subtitle}</p>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-6 overflow-x-auto pb-4 px-4 snap-x snap-mandatory scrollbar-hide">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index} 
              className="flex-shrink-0 w-[320px] md:w-[400px] p-6 bg-card rounded-2xl shadow-sm border snap-center"
            >
              <Quote className="w-8 h-8 text-primary/30 mb-4" />
              <p className="text-foreground mb-4">"{testimonial.content}"</p>
              <div className="flex items-center gap-3">
                {testimonial.avatar && (
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                )}
                <div className="flex-1">
                  <p className="font-semibold text-foreground text-sm">{testimonial.name}</p>
                  {testimonial.location && (
                    <p className="text-muted-foreground text-xs">{testimonial.location}</p>
                  )}
                </div>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={cn(
                        "w-4 h-4",
                        i < testimonial.rating ? "text-amber-500 fill-amber-500" : "text-muted"
                      )} 
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Default variant
  return (
    <section className={cn(
      "section-padding",
      variant === 'alt-bg' ? "bg-secondary/30" : "bg-background"
    )}>
      <div className="container mx-auto px-4">
        {title && (
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className={cn(
              "font-display font-bold text-foreground mb-4",
              isSerif ? "text-3xl md:text-4xl italic" : "text-3xl md:text-4xl"
            )}>
              {title}
            </h2>
            {subtitle && (
              <p className="text-muted-foreground text-lg">{subtitle}</p>
            )}
          </div>
        )}

        <div className={cn(
          "grid gap-8",
          testimonials.length === 1 ? "max-w-2xl mx-auto" :
          testimonials.length === 2 ? "md:grid-cols-2 max-w-4xl mx-auto" :
          "md:grid-cols-2 lg:grid-cols-3"
        )}>
          {testimonials.map((testimonial, index) => (
            <div 
              key={index} 
              className={cn(
                "p-6",
                isMinimal ? "border-l-2 border-foreground" :
                isPlayful ? "bg-secondary/50 rounded-3xl" :
                "bg-card rounded-2xl shadow-sm border"
              )}
            >
              {!isMinimal && (
                <Quote className="w-8 h-8 text-primary/30 mb-4" />
              )}
              <p className={cn(
                "text-foreground mb-4",
                isSerif ? "font-serif text-lg italic" : "text-base"
              )}>
                "{testimonial.content}"
              </p>
              <div className="flex items-center gap-3">
                {testimonial.avatar && (
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                )}
                <div>
                  <p className="font-semibold text-foreground text-sm">
                    {testimonial.name}
                  </p>
                  {testimonial.location && (
                    <p className="text-muted-foreground text-xs">
                      {testimonial.location}
                    </p>
                  )}
                </div>
                {testimonial.rating > 0 && (
                  <div className="flex ml-auto">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={cn(
                          "w-4 h-4",
                          i < testimonial.rating ? "text-amber-500 fill-amber-500" : "text-muted"
                        )} 
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}